import { useEffect, useMemo, useState } from "react";

export const DEFAULT_PAGE_SIZE_OPTIONS = Object.freeze([10, 25, 50]);

export function getPageCount(totalItems, pageSize) {
  return Math.max(1, Math.ceil(Math.max(0, totalItems) / Math.max(1, pageSize)));
}

export function clampPage(page, totalItems, pageSize) {
  return Math.min(Math.max(1, Number(page) || 1), getPageCount(totalItems, pageSize));
}

export function paginate(items, page, pageSize) {
  const safeItems = Array.isArray(items) ? items : [];
  const safePageSize = Math.max(1, Number(pageSize) || 1);
  const safePage = clampPage(page, safeItems.length, safePageSize);
  const start = (safePage - 1) * safePageSize;
  return safeItems.slice(start, start + safePageSize);
}

/** Reusable in-memory pagination for already-loaded client collections. */
export default function useClientPagination(items, { initialPageSize = 10 } = {}) {
  const safeItems = Array.isArray(items) ? items : [];
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const pageCount = getPageCount(safeItems.length, pageSize);
  const currentPage = clampPage(page, safeItems.length, pageSize);

  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);

  const pageItems = useMemo(
    () => paginate(safeItems, currentPage, pageSize),
    [safeItems, currentPage, pageSize],
  );

  const changePageSize = (nextSize) => {
    setPageSize(Math.max(1, Number(nextSize) || initialPageSize));
    setPage(1);
  };

  return {
    page: currentPage,
    pageSize,
    pageCount,
    totalItems: safeItems.length,
    pageItems,
    setPage,
    setPageSize: changePageSize,
    resetPage: () => setPage(1),
  };
}
